import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import Button from '../Button/Button';
import Feedback from '../Feedback/Feedback';
import Content from '../Content/Content';
import cancelAppeal, {
  cancelAppealReset,
} from '../../store/appealCancelData/actions';
import {
  appealCancelDataErrorSelector,
  appealCancelDataLoadingSelector,
  appealCancelDataSuccessSelector,
} from '../../store/appealCancelData/selectors';
import { goToHomePage } from '../../store/step/actions';
import {
  sendFeedback,
  sendStats,
  CLICK_CANCEL_APPEAL,
} from '../../resources/metrics';

import Print from '../../assets/Print.svg';
import LogoIcon from '../../assets/Logo.svg';

import './Cancel.pcss';

const CancelContent = ({
  isInconsistency,
  serviceNumber,
  isError,
  goToHomePage,
  isLoading,
  handleCancelAppeal,
}) => (
  <Content>
    <Content.Title>
      {isInconsistency
        ? 'Отзыв заявления о несоответствии бланков'
        : 'Отзыв апелляции о несогласии с выставленными баллами ГИА'}
    </Content.Title>
    <Content.Text>Номер вашей заявки {' ' + serviceNumber}</Content.Text>
    {isError ? (
      <div className="Cancel__Error">
        <h2 className="Cancel__Subtitle">Доступ закрыт</h2>
        <Content.Text>
          Вы не можете отзывать чужие {isInconsistency ? 'заявки' : 'апелляции'}
          .
        </Content.Text>
        <Content.Button>
          <Button primary onClick={() => goToHomePage()}>
            Перейти к услуге
          </Button>
        </Content.Button>
      </div>
    ) : (
      <Button
        primary
        progress={isLoading || undefined}
        onClick={handleCancelAppeal}
      >
        {isInconsistency ? 'Отозвать заявление' : 'Отозвать апелляцию'}
      </Button>
    )}
  </Content>
);

const Cancel = ({
  serviceNumber,
  isLoading,
  isError,
  isSuccess: isSuccessProp,
  cancelAppealReset,
  goToHomePage,
  cancelAppeal,
  type,
}) => {
  useEffect(() => {
    return () => {
      cancelAppealReset();
    };
  }, []);

  const handleCancelAppeal = () => {
    cancelAppeal(serviceNumber);
    sendStats(CLICK_CANCEL_APPEAL());
  };

  const isSuccess =
    isSuccessProp || window.location.pathname.includes('success-cancel');
  const isInconsistency = type === 'inconsistencyOfBlanks';

  // typograf-enable
  return (
    <div className="Result">
      {isSuccess ? (
        <div className="Result__Wrapper">
          <div className="Cancel" />
          <div className="Result__Print">
            <LogoIcon className="Result__Print-logo" />
            <h1 className="Result__Title">
              Подать апелляцию ГИА (ЕГЭ, ОГЭ, ГВЭ) в Москве
            </h1>
          </div>
          <Content>
            <Content.Title>
              {isInconsistency ? 'Заявление отозвано' : 'Апелляция отозвана'}
            </Content.Title>
            {serviceNumber && (
              <Content.Text>
                {`Номер вашей заявки `}
                <span className="Cancel__Number">{serviceNumber}</span>
                <Print
                  className="Cancel__Print-icon Result__NoPrint"
                  onClick={() => window.print()}
                />
              </Content.Text>
            )}
            <Content.Subtitle>Что дальше</Content.Subtitle>
            <Content.Text>
              <p className="Result__Paragraph">
                {`Статус ${
                  isInconsistency ? 'заявки' : 'апелляции'
                } изменится в `}
                <a
                  className="Link"
                  rel="noopener noreferrer"
                  target="_blank"
                  href="https://my.mos.ru/my/#/"
                >
                  Личном кабинете
                </a>{' '}
                через несколько минут.
              </p>
              <p className="Result__Paragraph">
                Если у вас возникнут какие-либо вопросы или затруднения —
                обратитесь в службу технической поддержки.
              </p>
            </Content.Text>
            <Feedback
              title="Оцените удобство сервиса"
              onClickFormSubmitButton={({ commentary }) =>
                sendFeedback(commentary, 'Отмена апелляции')
              }
              onRatingStarClick={({ rating }) =>
                sendFeedback(rating, 'Отмена апелляции')
              }
            />
            <div className="Result__NoPrint">
              <Content.Subtitle>Полезно знать</Content.Subtitle>
            </div>
            <Content.Link>
              <a
                className="Link"
                href="https://mos.ru/otvet-obrazovanie/kak-uznat-raspisanie-ege-oge-i-gve/"
              >
                Как узнать расписание ЕГЭ, ОГЭ и ГВЭ
              </a>
            </Content.Link>
            <Content.Link>
              <a
                className="Link"
                href="https://mos.ru/otvet-obrazovanie/kak-zapisat-rebenka-v-shkolu/"
              >
                Как перевести ребенка в другую школу
              </a>
            </Content.Link>
            <Content.Link>
              <a
                className="Link"
                href="https://www.mos.ru/otvet-obrazovanie/kak-dosrochno-sdat-ege-oge-ili-gve/"
              >
                Как досрочно сдать ЕГЭ, ОГЭ или ГВЭ
              </a>
            </Content.Link>
          </Content>
          <div className="Result__Print Result__PrintInfo">
            <p className="Result__PrintAdditional">
              Подача апелляции о несогласии с выставленными баллами
              государственной итоговой аттестации (ЕГЭ, ОГЭ, ГВЭ)
            </p>
            <p className="Result__PrintLink">
              www.mos.ru/services/podat-apellyatsiyu-na-rezultaty-gia/
            </p>
          </div>
        </div>
      ) : (
        <div className="Recall">
          <CancelContent
            isInconsistency={isInconsistency}
            serviceNumber={serviceNumber}
            isError={isError}
            goToHomePage={goToHomePage}
            isLoading={isLoading}
            handleCancelAppeal={handleCancelAppeal}
          />
        </div>
      )}
    </div>
  );
};

Cancel.defaultProps = {
  isLoading: false,
  isError: false,
  isSuccess: false,
};

Cancel.propTypes = {
  cancelAppeal: PropTypes.func.isRequired,
  cancelAppealReset: PropTypes.func.isRequired,
  goToHomePage: PropTypes.func.isRequired,
  serviceNumber: PropTypes.string.isRequired,
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
  isSuccess: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  isLoading: appealCancelDataLoadingSelector(state),
  isError: appealCancelDataErrorSelector(state),
  isSuccess: appealCancelDataSuccessSelector(state),
});

export default connect(mapStateToProps, (dispatch) =>
  bindActionCreators(
    {
      cancelAppeal,
      goToHomePage,
      cancelAppealReset,
    },
    dispatch,
  ),
)(Cancel);
